import {
  getActionsRoutine,
  refreshActionsRoutine,
  getActionStatsRoutine,
  getActionSectionRoutine,
  getActionRoutine,
  createActionRoutine,
  updateActionRoutine,
  convertActionToTaskRoutine,
  dismissActionRoutine,
  reopenActionRoutine,
  remedialRefreshedRoutine,
  bulkDismissActionsRoutine,
  bulkConvertActionsRoutine,
  bulkUpdateActionsRoutine,
} from './actions'
import { paramsKey, listParamsKey } from './paramsKey'

export const EMPTY_SECTION = { items: [], total: 0, loading: false, loaded: false, error: false }

export const initialState = {
  actions: [],
  actionsMeta: null,
  actionsLoading: false,
  actionsError: null,
  stats: null,
  statsLoading: false,
  statsError: false,
  statsKey: null,
  statsRequestKey: null,
  statsByKey: {},
  statsParamsByKey: {},
  statsRequestIds: {},
  sections: {},
  sectionRequestKeys: {},
  listRequestKey: null,
  currentAction: null,
  currentActionLoading: false,
  currentActionError: null,
  mutationLoading: false,
  pendingMutationIds: [],
  refreshedVersion: 0,
  mutationError: null,
  lastMutatedAction: null,
}

const KEEP_WHEN_MISSING = ['linkedTasks', 'sourceTask', 'taskSummary']

export const mergeAction = (existing, updated) => {
  const merged = { ...existing, ...updated }
  KEEP_WHEN_MISSING.forEach(key => {
    if ((updated[key] === null || updated[key] === undefined) && existing?.[key] != null) merged[key] = existing[key]
  })
  return merged
}

const replaceAction = (list, updated) =>
  list.map(a => (a.id === updated.id ? mergeAction(a, updated) : a))

const patchSections = (sections, updated) =>
  Object.fromEntries(Object.entries(sections).map(([key, section]) => [key, { ...section, items: replaceAction(section.items || [], updated) }]))

const markIds = (list, ids, patch) => {
  const idSet = new Set(ids)
  return list.map(a => (idSet.has(a.id) ? { ...a, ...patch } : a))
}

const setSection = (state, key, patch) => ({
  ...state,
  sections: { ...state.sections, [key]: { ...(state.sections[key] || EMPTY_SECTION), ...patch } },
})

const MUTATION_ROUTINES = [
  createActionRoutine,
  updateActionRoutine,
  convertActionToTaskRoutine,
  dismissActionRoutine,
  reopenActionRoutine,
  bulkDismissActionsRoutine,
  bulkConvertActionsRoutine,
  bulkUpdateActionsRoutine,
]
const MUTATION_STARTED = MUTATION_ROUTINES.map(r => r.REQUEST)
const MUTATION_ENDED = MUTATION_ROUTINES.flatMap(r => [r.SUCCESS, r.FAILURE, r.FULFILL])
const UNTRACKED_REQUEST = 'untracked'

const trackMutations = (state, action) => {
  const pending = state.pendingMutationIds || []
  const id = action.meta?.requestId ?? UNTRACKED_REQUEST
  if (MUTATION_STARTED.includes(action.type)) {
    const next = pending.includes(id) ? pending : [...pending, id]
    return { ...state, pendingMutationIds: next, mutationLoading: true }
  }
  if (MUTATION_ENDED.includes(action.type)) {
    if (!pending.includes(id)) return state
    const next = pending.filter(p => p !== id)
    return { ...state, pendingMutationIds: next, mutationLoading: next.length > 0 }
  }
  return state
}

export default function reducer(state = initialState, action) {
  state = trackMutations(state, action)
  switch (action.type) {
    case getActionsRoutine.REQUEST: {
      const isPageOne = (action.payload?.page ?? 1) === 1
      return {
        ...state,
        actionsLoading: true,
        actionsError: null,
        actions: isPageOne ? [] : state.actions,
        actionsMeta: isPageOne ? null : state.actionsMeta,
        listRequestKey: listParamsKey(action.payload),
      }
    }
    case getActionsRoutine.SUCCESS: {
      if (action.payload?.requestKey !== undefined && action.payload.requestKey !== state.listRequestKey) return state
      const incoming = action.payload?.data ?? []
      const meta = action.payload?.meta ?? null
      const isAppend = (meta?.currentPage ?? 1) > 1
      const merged = isAppend
        ? [...state.actions, ...incoming.filter(i => !state.actions.some(a => a.id === i.id))]
        : incoming
      return { ...state, actionsLoading: false, actions: merged, actionsMeta: meta }
    }
    case getActionsRoutine.FAILURE:
      return { ...state, actionsLoading: false, actionsError: action.payload }

    case refreshActionsRoutine.REQUEST:
      return { ...state, actionsError: null, listRequestKey: listParamsKey(action.payload) }
    case refreshActionsRoutine.SUCCESS: {
      if (action.payload?.requestKey !== undefined && action.payload.requestKey !== state.listRequestKey) return state
      return { ...state, actions: action.payload?.data ?? [], actionsMeta: action.payload?.meta ?? state.actionsMeta }
    }
    case refreshActionsRoutine.FAILURE:
      return { ...state, actionsError: action.payload }

    case getActionStatsRoutine.REQUEST: {
      const key = paramsKey(action.payload)
      return {
        ...state,
        statsLoading: true,
        statsError: false,
        statsRequestKey: key,
        statsParamsByKey: { ...state.statsParamsByKey, [key]: action.payload || {} },
        statsRequestIds: action.meta?.requestId ? { ...state.statsRequestIds, [key]: action.meta.requestId } : state.statsRequestIds,
      }
    }
    case getActionStatsRoutine.SUCCESS: {
      const key = action.meta?.statsKey ?? state.statsRequestKey
      if (action.meta?.requestId && state.statsRequestIds?.[key] && state.statsRequestIds[key] !== action.meta.requestId) return state
      const stats = action.payload?.data ?? null
      return {
        ...state,
        statsLoading: false,
        statsError: false,
        stats,
        statsKey: key,
        statsByKey: { ...state.statsByKey, [key]: stats },
      }
    }
    case getActionStatsRoutine.FAILURE:
      return { ...state, statsLoading: false, statsError: true }

    case getActionSectionRoutine.REQUEST: {
      const key = action.payload?.key
      return {
        ...setSection(state, key, { loading: true, error: false }),
        sectionRequestKeys: { ...state.sectionRequestKeys, [key]: action.payload?.requestKey },
      }
    }
    case getActionSectionRoutine.SUCCESS: {
      const key = action.payload?.key
      if (action.payload?.requestKey !== undefined && action.payload.requestKey !== state.sectionRequestKeys?.[key]) return state
      return setSection(state, key, { items: action.payload?.items ?? [], total: action.payload?.total ?? 0, loading: false, loaded: true, error: false, requestKey: action.payload?.requestKey })
    }
    case getActionSectionRoutine.FAILURE: {
      const key = action.payload?.key
      if (action.payload?.requestKey !== undefined && action.payload.requestKey !== state.sectionRequestKeys?.[key]) return state
      return setSection(state, key, { loading: false, loaded: true, error: true, requestKey: action.payload?.requestKey })
    }

    case getActionRoutine.REQUEST:
      return { ...state, currentActionLoading: true, currentActionError: null }
    case getActionRoutine.SUCCESS:
      return { ...state, currentActionLoading: false, currentAction: action.payload?.data ?? null }
    case getActionRoutine.FAILURE:
      return { ...state, currentActionLoading: false, currentActionError: action.payload }

    case createActionRoutine.REQUEST:
    case updateActionRoutine.REQUEST:
    case convertActionToTaskRoutine.REQUEST:
    case dismissActionRoutine.REQUEST:
    case reopenActionRoutine.REQUEST:
    case bulkDismissActionsRoutine.REQUEST:
    case bulkConvertActionsRoutine.REQUEST:
    case bulkUpdateActionsRoutine.REQUEST:
      return { ...state, mutationError: null }

    case createActionRoutine.SUCCESS: {
      const created = action.payload?.data
      if (!created) return state
      const exists = state.actions.some(a => a.id === created.id)
      return {
        ...state,
        actions: exists ? replaceAction(state.actions, created) : [created, ...state.actions],
        actionsMeta: !exists && state.actionsMeta?.total != null ? { ...state.actionsMeta, total: state.actionsMeta.total + 1 } : state.actionsMeta,
      }
    }

    case remedialRefreshedRoutine.SUCCESS: {
      const updated = action.payload?.data
      if (!updated) return state
      return {
        ...state,
        actions: replaceAction(state.actions, updated),
        sections: patchSections(state.sections, updated),
        currentAction: state.currentAction?.id === updated.id ? mergeAction(state.currentAction, updated) : state.currentAction,
        lastMutatedAction: updated,
        refreshedVersion: (state.refreshedVersion || 0) + 1,
      }
    }

    case updateActionRoutine.SUCCESS:
    case convertActionToTaskRoutine.SUCCESS:
    case dismissActionRoutine.SUCCESS:
    case reopenActionRoutine.SUCCESS: {
      const updated = action.payload?.data
      if (!updated) return state
      return {
        ...state,
        actions: replaceAction(state.actions, updated),
        sections: patchSections(state.sections, updated),
        currentAction: state.currentAction?.id === updated.id ? mergeAction(state.currentAction, updated) : state.currentAction,
        lastMutatedAction: updated,
      }
    }

    case bulkDismissActionsRoutine.SUCCESS:
      return { ...state, actions: markIds(state.actions, action.payload?.data?.dismissedIds || [], { status: 'dismissed', stage: 'dismissed' }) }
    case bulkConvertActionsRoutine.SUCCESS:
      return { ...state, actions: markIds(state.actions, action.payload?.data?.convertedIds || [], { status: 'converted', stage: 'task_created' }) }

    case createActionRoutine.FAILURE:
    case updateActionRoutine.FAILURE:
    case convertActionToTaskRoutine.FAILURE:
    case dismissActionRoutine.FAILURE:
    case reopenActionRoutine.FAILURE:
    case bulkDismissActionsRoutine.FAILURE:
    case bulkConvertActionsRoutine.FAILURE:
    case bulkUpdateActionsRoutine.FAILURE:
      return { ...state, mutationError: action.payload }

    default:
      return state
  }
}
